'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { MessageCircle, X, Send, Minimize2, Maximize2, Loader2, Sparkles } from 'lucide-react';
import Image from 'next/image';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { getPlaybookContext, getSuggestedPrompts } from '@/lib/ai/supportPlaybooks';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  mascot_pose?: string;
  cta?: string;
}

// Stable visitor id — one ACP session per visitor on the sales persona
// (web:<vid>), so returning visitors resume their conversation (§24.3).
function getSalesVisitorId(): string {
  try {
    const existing = localStorage.getItem('ta_sales_vid');
    if (existing) return existing;
    const vid = crypto.randomUUID();
    localStorage.setItem('ta_sales_vid', vid);
    return vid;
  } catch {
    return crypto.randomUUID();
  }
}

function collectUtm(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref']) {
      const val = params.get(key);
      if (val) out[key] = val;
    }
    if (document.referrer) out.referrer = document.referrer;
  } catch { /* SSR-safe */ }
  return out;
}

const SALES_CTA_LINKS: Record<string, { href: string; label: string }> = {
  demo: { href: '/contact?intent=demo', label: 'Book a demo' },
  trial: { href: '/contact?intent=trial', label: 'Start a free trial' },
  booking: { href: '/contact?intent=demo', label: 'Book a demo slot' },
  pricing: { href: '/pricing', label: 'See pricing' },
  contact: { href: '/contact', label: 'Contact us' },
};

interface MascotAsset {
  asset_key: string;
  display_name: string;
  file_url: string;
  use_case: string;
  position: string;
  pointing_direction: string | null;
}

interface PublicAIChatbotProps {
  initialOpen?: boolean;
}

export default function PublicAIChatbot({ initialOpen = false }: PublicAIChatbotProps) {
  const pathname = usePathname();
  const { currentTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [mascotAssets, setMascotAssets] = useState<Record<string, MascotAsset>>({});
  const [currentMascot, setCurrentMascot] = useState<string>('thumbs_up');
  const [mounted, setMounted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Set mounted state after hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  // Whitelist approach: Only show chatbot on specific public pages
  const allowedPaths = [
    '/',           // Homepage
    '/privacy',
    '/products',   // Unified catalog — modules + industry packages
    '/legal',
    '/contact',
    '/case-studies',
    '/about'
  ];
  
  // Check if current path is allowed (exact match or starts with allowed path + /)
  const isAllowedPath = allowedPaths.some(path => {
    if (path === '/') {
      return pathname === '/';
    }
    return pathname === path || pathname?.startsWith(`${path}/`);
  });

  // Define loadMascotAssets function before using it in useEffect
  const loadMascotAssets = async () => {
    try {
      // For public display, we'll use hardcoded mascot URLs
      const publicMascots: Record<string, MascotAsset> = {
        'thumbs_up': {
          asset_key: 'thumbs_up',
          display_name: 'Thumbs Up',
          file_url: 'https://staticfileserver.totalaccess.co.za/files/0c588cc7f03ddea8723090749d4099378276eb94485ad35a112283922676bb79.png',
          use_case: 'chat_avatar',
          position: 'bottom_right',
          pointing_direction: null
        },
        'intro_dude': {
          asset_key: 'intro_dude',
          display_name: 'Intro Pose',
          file_url: 'https://staticfileserver.totalaccess.co.za/files/628cab51c28f5e5bf0b55242306ef160314ca93c78b2c3cb5da6e9cd6ccef9e5.png',
          use_case: 'welcome',
          position: 'center',
          pointing_direction: null
        },
        'jumping_joy': {
          asset_key: 'jumping_joy',
          display_name: 'Jumping with Joy',
          file_url: 'https://staticfileserver.totalaccess.co.za/files/8b929f458c54014b2ad9e1070f1e43151299f05942161e4433146b4bc77c104a.png',
          use_case: 'success',
          position: 'center',
          pointing_direction: null
        }
      };
      setMascotAssets(publicMascots);
    } catch (error) {
      // Suppress offline errors - they're expected and handled by fetch patch
      const fetchError = error as { isOfflineError?: boolean; message?: string };
      if (!fetchError?.isOfflineError &&
          !fetchError?.message?.includes('Network unavailable') &&
          !fetchError?.message?.includes('offline')) {
        console.error('Failed to load mascot assets:', error);
      }
    }
  };

  // Load mascot assets on mount
  useEffect(() => {
    loadMascotAssets();
  }, []);

  // Load chat state from localStorage (auto-open disabled - user must click to open)
  useEffect(() => {
    // Chatbot stays closed by default - user must click to open
    // No auto-open behavior
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize conversation with welcome message
  useEffect(() => {
    if (isOpen && !hasInitialized && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "👋 Hi there! I'm Total Assistant, your guide to TotalAccess.\n\nI can help you learn about our platform, answer questions about features, pricing, and how TotalAccess can transform your business.\n\nFeel free to ask me anything - I'm here to help you discover how TotalAccess can work for you!",
        timestamp: new Date(),
        mascot_pose: 'intro_dude'
      }]);
      setCurrentMascot('intro_dude');
      setHasInitialized(true);
    }
  }, [isOpen, hasInitialized, messages.length]);

  // Quick actions from playbooks (must be before any early return to respect hooks order)
  const quickActions = useMemo(() => {
    const pageBasedPrompts = getSuggestedPrompts(true, undefined, false);
    return pageBasedPrompts.length > 0 ? pageBasedPrompts : [
      "What is TotalAccess?",
      "Show me pricing plans",
      "What modules are available?",
      "How does it compare to competitors?",
      "Can I see a demo?",
      "How do I get started?"
    ];
  }, []);

  // Early return AFTER all hooks are called - only show on allowed paths
  if (!isAllowedPath) {
    return null;
  }

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('totalaccess_chatbot_dismissed', new Date().toISOString());
  };

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputMessage.trim();
    if (!textToSend || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: textToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Fetch semantic context (public mode — playbooks + marketing configs only).
      // The endpoint requires auth, so unauthenticated visitors skip it and
      // use the local playbook fallback directly instead of a guaranteed 401.
      let semanticContext = '';
      const hasAuthToken = Boolean(authService.getTokens()?.access_token);
      try {
        const ctxResponse = hasAuthToken ? await fetch('/api/v1/ai/semantic-context', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            topic: textToSend,
            isPublic: true,
            includeTrainingContext: false,
            includeMarketingContext: true,
            includeSemanticSearch: false,
            includePlaybooks: true,
          }),
        }) : null;
        if (ctxResponse?.ok) {
          const ctxData = await ctxResponse.json();
          if (ctxData.success && ctxData.context) {
            semanticContext = ctxData.context;
          }
        }
      } catch (err) {
        // Non-critical — fall back to local playbooks
        semanticContext = getPlaybookContext(true, undefined, false, false, textToSend);
      }

      // Build context for public mode
      const context = {
        current_page: typeof window !== 'undefined' ? window.location.pathname : '/',
        is_public: true,
        user_info: {
          name: 'Guest'
        },
        session_info: {
          message_count: messages.length + 1, // Include current user message
          has_history: messages.length > 0
        },
        semantic_context: semanticContext || undefined,
      };

      console.log('📤 Public AI Chat Request:', {
        currentPage: context.current_page,
        messageCount: context.session_info.message_count
      });

      const response = await fetch('/api/v1/public/ai-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: userMessage.content,
          conversation_history: messages.map(m => ({
            role: m.role,
            content: m.content
          })),
          context: { ...context, utm: collectUtm() },
          visitor_id: getSalesVisitorId(),
          ai_provider: 'total-vision' // Self-hosted — zero per-call cost
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      if (data.success) {
        const assistantMessage: Message = {
          role: 'assistant',
          content: data.response.question || data.response.message || data.response.summary || data.response || 'I understand. How else can I help you?',
          timestamp: new Date(),
          mascot_pose: data.response.mascot_pose || 'thumbs_up', // Use mascot from response or default
          ...(typeof data.cta === 'string' && SALES_CTA_LINKS[data.cta] ? { cta: data.cta } : {}),
        };
        setMessages(prev => [...prev, assistantMessage]);
        setCurrentMascot(assistantMessage.mascot_pose || 'thumbs_up');
      } else {
        throw new Error(data.error || 'Failed to get response');
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Please try again or contact our sales team directly.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Render message content with video/resource links
  const renderMessageContent = (content: string) => {
    // Parse [VIDEO: Title](module_key) format
    const videoLinkRegex = /\[VIDEO:\s*([^\]]+)\]\(([^)]+)\)/g;
    const parts: (string | React.ReactNode)[] = [];
    let lastIndex = 0;
    let match;

    while ((match = videoLinkRegex.exec(content)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index));
      }

      // Add video link button
      const videoTitle = match[1].trim();
      const moduleKey = match[2].trim();
      parts.push(
        <button
          key={match.index}
          onClick={() => {
            // Navigate to resources page filtered by module
            window.open(`/resources?module=${moduleKey}`, '_blank');
          }}
          className={`inline-flex items-center gap-1 px-3 py-1 my-1 rounded-lg font-medium transition-colors ${
            currentTheme.mode === 'dark'
              ? 'bg-purple-900/50 hover:bg-purple-800/50 text-purple-300 border border-purple-700'
              : 'bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200'
          }`}
        >
          <span className="text-lg">📹</span>
          <span className="text-sm">{videoTitle}</span>
        </button>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 w-16 h-16 text-white rounded-full shadow-2xl flex items-center justify-center z-50 transition-all duration-300 hover:scale-110"
        style={mounted ? {
          background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.primaryDark})`
        } : {
          background: 'linear-gradient(135deg, #ff0099, #cc0077)'
        }}
        aria-label="Open chat"
        suppressHydrationWarning
      >
        {mascotAssets['thumbs_up'] ? (
          <Image
            src={mascotAssets['thumbs_up'].file_url}
            alt="AI Assistant"
            width={48}
            height={48}
            className="rounded-full"
            priority
          />
        ) : (
          <MessageCircle className="w-8 h-8" />
        )}
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-6 left-6 z-50 transition-all duration-300 ${
        isMinimized ? 'w-16 h-16' : 'w-[400px] h-[600px]'
      }`}
    >
      {isMinimized ? (
        <button
          onClick={() => setIsMinimized(false)}
          className="w-full h-full text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110"
          style={mounted ? {
            background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.primaryDark})`
          } : {
            background: 'linear-gradient(135deg, #ff0099, #cc0077)'
          }}
          suppressHydrationWarning
        >
          {mascotAssets[currentMascot] ? (
            <Image
              src={mascotAssets[currentMascot].file_url}
              alt="AI Assistant"
              width={48}
              height={48}
              className="rounded-full"
            />
          ) : (
            <MessageCircle className="w-8 h-8" />
          )}
          {messages.length > 0 && (
            <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold">
              {messages.filter(m => m.role === 'assistant').length}
            </span>
          )}
        </button>
      ) : (
        <div 
          className="w-full h-full rounded-2xl shadow-2xl flex flex-col overflow-hidden border"
          style={mounted ? {
            backgroundColor: currentTheme.mode === 'dark' ? currentTheme.colors.surfaceDark : currentTheme.colors.surface,
            borderColor: currentTheme.mode === 'dark' ? '#374151' : '#e5e7eb'
          } : {
            backgroundColor: '#1f2937',
            borderColor: '#374151'
          }}
          suppressHydrationWarning
        >
          {/* Header */}
          <div 
            className="px-6 py-4 flex items-center justify-between"
            style={mounted ? {
              background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.primaryDark})`
            } : {
              background: 'linear-gradient(135deg, #ff0099, #cc0077)'
            }}
            suppressHydrationWarning
          >
            <div className="flex items-center gap-3">
              {mascotAssets[currentMascot] ? (
                <Image
                  src={mascotAssets[currentMascot].file_url}
                  alt="AI Assistant"
                  width={40}
                  height={40}
                  className="rounded-full bg-white/20 p-1"
                />
              ) : (
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
              )}
              <div>
                <h3 className="text-white font-semibold">Total Assistant</h3>
                <p className="text-white/80 text-xs">Always here to help</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(true)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Minimize"
              >
                <Minimize2 className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div 
            className="flex-1 overflow-y-auto p-4 space-y-4"
            style={{
              backgroundColor: currentTheme.mode === 'dark' ? currentTheme.colors.background : '#f9fafb'
            }}
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex items-start gap-2 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.role === 'assistant' && message.mascot_pose && mascotAssets[message.mascot_pose] && (
                  <Image
                    src={mascotAssets[message.mascot_pose].file_url}
                    alt="Mascot"
                    width={32}
                    height={32}
                    className="flex-shrink-0 rounded-full mt-1"
                  />
                )}
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-3`}
                  style={{
                    backgroundColor: message.role === 'user'
                      ? currentTheme.colors.primary
                      : currentTheme.mode === 'dark'
                      ? currentTheme.colors.surface
                      : '#ffffff',
                    color: message.role === 'user'
                      ? '#ffffff'
                      : currentTheme.mode === 'dark'
                      ? '#f9fafb'
                      : '#111827',
                    border: message.role === 'assistant' ? `1px solid ${currentTheme.mode === 'dark' ? '#374151' : '#e5e7eb'}` : 'none'
                  }}
                >
                  <div className="text-sm whitespace-pre-wrap">
                    {message.role === 'assistant' ? renderMessageContent(message.content) : message.content}
                  </div>
                  {message.cta && SALES_CTA_LINKS[message.cta] && (
                    <button
                      onClick={() => { window.location.href = SALES_CTA_LINKS[message.cta!].href; }}
                      className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        currentTheme.mode === 'dark'
                          ? 'bg-pink-600/30 hover:bg-pink-600/50 text-pink-200 border border-pink-500/40'
                          : 'bg-pink-50 hover:bg-pink-100 text-pink-700 border border-pink-200'
                      }`}
                    >
                      {SALES_CTA_LINKS[message.cta].label} →
                    </button>
                  )}
                  <p className={`text-xs mt-1 ${
                    message.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                  }`}>
                    {(() => {
                      const timestamp = message.timestamp instanceof Date 
                        ? message.timestamp 
                        : new Date(message.timestamp);
                      return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    })()}
                  </p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-gray-800 rounded-2xl px-4 py-3 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">Thinking...</p>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length === 1 && (
            <div className="px-4 py-2 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => handleSendMessage(action)}
                    className="text-xs px-3 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full transition-colors"
                  >
                    {action}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything..."
                disabled={isLoading}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 disabled:opacity-50"
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputMessage.trim() || isLoading}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              Powered by AI • <a href="/contact" className="text-blue-600 hover:underline">Contact Sales</a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
